"use client"

import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import { cn } from "@/utils/helpers"

function ScrollArea({
  className,
  barClassName,
  children,
  scrollbars = "vertical",
  onViewportScroll,
  viewportRef,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  barClassName?: string
  onViewportScroll?: React.UIEventHandler<HTMLDivElement>
  viewportRef?: React.Ref<HTMLDivElement>
  /**
   * @default "vertical"
   */
  scrollbars?: "vertical" | "horizontal" | "both"
}) {
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn("relative", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        className="focus-visible:ring-ring/50 size-full rounded-[inherit] outline-none transition-[color,box-shadow] focus-visible:outline-1 focus-visible:ring-[3px]"
        onScroll={onViewportScroll}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {scrollbars !== "vertical" ? (
        <ScrollBar orientation="horizontal" className={barClassName} />
      ) : null}
      {scrollbars !== "horizontal" ? (
        <ScrollBar orientation="vertical" className={barClassName} />
      ) : null}
      {scrollbars === "both" ? <ScrollAreaPrimitive.Corner /> : null}
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none select-none transition-colors",
        orientation === "vertical" &&
          "h-full w-2.5 -translate-x-[7px] border-l border-l-transparent px-px pb-3 pt-1",
        orientation === "horizontal" &&
          "h-2.5 -translate-y-[7px] flex-col border-t border-t-transparent py-px pl-1 pr-3",
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-scrim relative flex-1 rounded-full opacity-75"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea, ScrollBar }
